import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ShieldAlert, ShieldCheck, RefreshCw, Clock, AlertTriangle, Zap, Ticket, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const EVENT_TYPE_CONFIG = {
  VELOCITY_BLOCK: { label: 'Velocity Block', color: 'bg-red-500', icon: Zap },
  RATE_LIMIT_HIT: { label: 'Rate Limit', color: 'bg-orange-500', icon: AlertTriangle },
  TICKET_LIMIT_EXCEEDED: { label: 'Ticket Limit', color: 'bg-yellow-500', icon: Ticket },
  TICKET_LIMIT_EXCEEDED_POST_PAYMENT: { label: 'Limit Post-Payment', color: 'bg-red-600', icon: CreditCard },
  REFUND_ISSUED: { label: 'Refund Issued', color: 'bg-emerald-500', icon: ShieldCheck },
  REFUND_FAILED: { label: 'Refund FAILED', color: 'bg-red-700', icon: ShieldAlert },
};

export default function SecurityLog() {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [resolvedFilter, setResolvedFilter] = useState('false');
  const [resolving, setResolving] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.append('event_type', typeFilter);
      if (resolvedFilter !== 'all') params.append('resolved', resolvedFilter);
      params.append('limit', '50');

      const [eventsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/security-events?${params}`),
        axios.get(`${API_URL}/api/admin/security-events/summary`),
      ]);
      setEvents(eventsRes.data.events || []);
      setSummary(summaryRes.data);
    } catch (err) {
      if (err.response?.status !== 403) {
        toast.error('Failed to load security events');
      }
    } finally {
      setLoading(false);
    }
  }, [typeFilter, resolvedFilter]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleResolve = async (securityEventId) => {
    setResolving(securityEventId);
    try {
      await axios.put(`${API_URL}/api/admin/security-events/${securityEventId}/resolve`);
      toast.success('Marked as reviewed');
      fetchEvents();
    } catch {
      toast.error('Failed to resolve event');
    } finally {
      setResolving(null);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); } catch { return ts; }
  };

  return (
    <div className="space-y-4" data-testid="security-log">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="py-3 px-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10">
                <ShieldAlert className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="unresolved-count">{summary.unresolved_total}</p>
                <p className="text-xs text-muted-foreground">Unresolved</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-3 px-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10">
                <Zap className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.velocity_blocks}</p>
                <p className="text-xs text-muted-foreground">Velocity Blocks</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-3 px-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <Ticket className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.ticket_limit_hits}</p>
                <p className="text-xs text-muted-foreground">Ticket Limits</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-3 px-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.rate_limit_hits}</p>
                <p className="text-xs text-muted-foreground">Rate Limits</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]" data-testid="security-type-filter">
            <SelectValue placeholder="Event type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="VELOCITY_BLOCK">Velocity Blocks</SelectItem>
            <SelectItem value="TICKET_LIMIT_EXCEEDED">Ticket Limits</SelectItem>
            <SelectItem value="TICKET_LIMIT_EXCEEDED_POST_PAYMENT">Limit Post-Payment</SelectItem>
            <SelectItem value="REFUND_ISSUED">Refunds Issued</SelectItem>
            <SelectItem value="REFUND_FAILED">Refunds Failed</SelectItem>
            <SelectItem value="RATE_LIMIT_HIT">Rate Limits</SelectItem>
          </SelectContent>
        </Select>
        <Select value={resolvedFilter} onValueChange={setResolvedFilter}>
          <SelectTrigger className="w-[180px]" data-testid="security-resolved-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="false">Unresolved</SelectItem>
            <SelectItem value="true">Resolved</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchEvents} disabled={loading} data-testid="security-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Events Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" />
            Security Events ({events.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-8">
              <ShieldCheck className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-muted-foreground">No security events found</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="security-events-list">
              {events.map((evt) => {
                const config = EVENT_TYPE_CONFIG[evt.event_type] || { label: evt.event_type, color: 'bg-gray-500', icon: AlertTriangle };
                const Icon = config.icon;
                return (
                  <div
                    key={evt.security_event_id}
                    className={`flex items-start justify-between p-3 rounded-lg border ${
                      evt.resolved ? 'border-border/50 opacity-60' : 'border-red-500/20 bg-red-500/5'
                    }`}
                    data-testid={`security-event-${evt.security_event_id}`}
                  >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${evt.resolved ? 'text-muted-foreground' : 'text-red-500'}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${config.color} text-white text-xs`}>{config.label}</Badge>
                          {evt.resolved && <Badge variant="outline" className="text-xs text-emerald-500">Reviewed</Badge>}
                        </div>
                        <p className="text-sm mt-1">{evt.reason}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                          {evt.email && <span>Email: {evt.email}</span>}
                          {evt.user_id && <span>User: {evt.user_id.slice(0, 12)}...</span>}
                          {evt.ip_address && <span>IP: {evt.ip_address}</span>}
                          {evt.event_id && <span>Event: {evt.event_id}</span>}
                        </div>
                        <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {formatTime(evt.timestamp)}
                          {evt.resolved_by && <span className="ml-2">Reviewed by: {evt.resolved_by.slice(0, 12)}...</span>}
                        </div>
                      </div>
                    </div>
                    {!evt.resolved && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-2 flex-shrink-0"
                        disabled={resolving === evt.security_event_id}
                        onClick={() => handleResolve(evt.security_event_id)}
                        data-testid={`resolve-${evt.security_event_id}`}
                      >
                        <ShieldCheck className="w-4 h-4 mr-1" />
                        {resolving === evt.security_event_id ? 'Saving...' : 'Mark Reviewed'}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
